import { PowerNames } from '../engine/JuniValues';
import type { Player } from './Player';
import { C } from './Player';

/** Base state. Mirrors JuniState.cs. */
export abstract class JuniState {
  abstract readonly name: string;
  constructor(protected juni: Player) {}
  onEnter(): void {}
  preProcess(_dt: number): void {}
  postProcess(_dt: number): void {}
  onExit(): void {}
}

export class IdleState extends JuniState {
  readonly name = 'Idle';
  override onEnter(): void {
    this.juni.playAnim('Idle');
    this.juni.jumps = 0;
  }
  override preProcess(): void {
    this.juni.dir = this.juni.moveDirection;
    if (this.juni.dir !== 0 && !this.juni.sticky) {
      this.juni.transitionState(new WalkRunState(this.juni, this.juni.walkRun));
    }
  }
  override postProcess(): void {
    const j = this.juni;
    if (j.game.input.isHeld('up') && j.canClimb) {
      j.vy = C.CLIMB_SPEED;
      j.transitionState(new ClimbState(j));
    }
    if (j.grounded) j.canFreeJump = true;
    if (j.game.input.justPressed('jump') && j.canJump && j.canFreeJump) {
      j.executeJump(undefined, false, true);
    } else if (!j.canFreeJump) {
      j.jumps = 1;
      j.transitionState(new FallState(j));
    }
  }
}

export class WalkRunState extends JuniState {
  readonly name = 'WalkRun';
  private sound: string;
  constructor(juni: Player, private walk_run: boolean) {
    super(juni);
    this.sound = walk_run ? 'Knytt Run (troupe)' : 'Knytt Walk (troupe)';
  }
  override onEnter(): void {
    this.juni.game.audio.startLoop(this.sound, true);
    this.juni.playAnim(this.walk_run ? 'Run' : 'Walk');
    this.juni.jumps = 0;
  }
  override preProcess(): void {
    const j = this.juni;
    j.dir = j.moveDirection;
    if (j.dir === 0 || j.sticky) j.transitionState(new IdleState(j));
    if (j.walkRun !== this.walk_run) j.transitionState(new WalkRunState(j, !this.walk_run));
  }
  override postProcess(): void {
    const j = this.juni;
    if (j.game.input.isHeld('up') && j.canClimb) {
      j.vy = C.CLIMB_SPEED;
      j.transitionState(new ClimbState(j));
    }
    if (j.grounded) j.canFreeJump = true;
    if (j.game.input.justPressed('jump') && j.canJump && j.canFreeJump) {
      j.executeJump(undefined, false, true);
    } else if (!j.grounded && j.didAirJump) {
      j.executeJump(undefined, true);
    } else if (!j.canFreeJump) {
      j.jumps = 1;
      j.transitionState(new FallState(j));
    }
  }
  override onExit(): void {
    this.juni.game.audio.stopLoop(this.sound);
  }
}

export class ClimbState extends JuniState {
  readonly name: string = 'Climb';
  override onEnter(): void {
    this.juni.playAnim('Climbing');
    this.juni.game.audio.startLoop('Knytt Climb (troupe)', true);
    this.juni.jumps = 0;
    this.juni.canFreeJump = false;
  }
  protected calcDir(): void {
    this.juni.dir = this.juni.moveDirection;
  }
  override preProcess(): void {
    const j = this.juni;
    this.calcDir();
    if (!j.canClimb) {
      j.transitionState(new FallState(j));
      j.justClimbed = true;
      j.canFreeJump = true;
    }
  }
  override postProcess(): void {
    const j = this.juni;
    if (j.game.input.isHeld('up')) j.vy = C.CLIMB_SPEED;
    else if (j.vy > 0) j.transitionState(new SlideState(j));
    if (j.game.input.justPressed('jump') && j.canJump) {
      j.vx = j.facingRight ? -C.CLIMB_JUMP_X_SPEED : C.CLIMB_JUMP_X_SPEED;
      j.executeJump();
    }
  }
  override onExit(): void {
    this.juni.game.audio.stopLoop('Knytt Climb (troupe)');
  }
}

export class SlideState extends ClimbState {
  override readonly name = 'Slide';
  override onEnter(): void {
    this.juni.playAnim('Sliding');
    this.juni.jumps = 0;
    this.juni.canFreeJump = false;
  }
  override preProcess(): void {
    const j = this.juni;
    this.calcDir();
    if (!j.canClimb) {
      j.transitionState(new FallState(j));
      j.justClimbed = true;
      j.canFreeJump = true;
    }
    if (j.grounded) j.transitionState(new IdleState(j));
  }
  override postProcess(): void {
    const j = this.juni;
    if (j.game.input.isHeld('up')) {
      j.vy = C.CLIMB_SPEED;
      j.transitionState(new ClimbState(j));
    } else if (!j.game.input.isHeld('down')) {
      j.vy = C.SLIDE_SPEED;
      j.game.audio.stopLoop('Knytt Slide');
    } else {
      j.game.audio.startLoop('Knytt Slide', true);
    }
    if (j.game.input.justPressed('jump') && j.canJump) {
      j.vx = j.facingRight ? -C.CLIMB_JUMP_X_SPEED : C.CLIMB_JUMP_X_SPEED;
      j.executeJump();
    }
  }
  override onExit(): void {
    this.juni.game.audio.stopLoop('Knytt Slide');
  }
}

export class JumpState extends JuniState {
  readonly name = 'Jump';
  override preProcess(): void {
    const j = this.juni;
    j.dir = j.moveDirection;
    if (j.didAirJump) j.executeJump(undefined, true);
  }
  override postProcess(): void {
    const j = this.juni;
    if (j.canClimb) j.transitionState(new ClimbState(j));
    else if (j.vy >= 0) j.transitionState(new FallState(j));
  }
}

export class FallState extends JuniState {
  readonly name = 'Fall';
  override onEnter(): void {
    this.juni.playAnim('StartFall');
  }
  override preProcess(): void {
    const j = this.juni;
    j.dir = j.moveDirection;
    if (j.didAirJump) j.executeJump(undefined, true);
  }
  override postProcess(): void {
    const j = this.juni;
    if (j.canClimb) j.transitionState(new ClimbState(j));
    if (j.grounded) {
      if (j.moveDirection !== 0) j.transitionState(new WalkRunState(j, j.walkRun));
      else j.transitionState(new IdleState(j));
      j.game.audio.sfx('Knytt Land (troupe)', true);
    }
  }
}

void PowerNames;
